import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Music, User, Hash, Sparkles, ChevronRight, Disc3, Eye, FileText, Mic2, ArrowLeft, ExternalLink } from 'lucide-react';
import axios from 'axios';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Color mapping for node types
const TYPE_COLORS = {
  word: '#00F0FF',
  theme: '#BD00FF',
  artist: '#0AFF64',
  song: '#FF0055',
};

const TYPE_ICONS = {
  word: Hash,
  theme: Sparkles,
  artist: User,
  song: Music,
};

const TYPE_LABELS = {
  word: 'Words',
  theme: 'Themes',
  artist: 'Artists',
  song: 'Songs',
};

// Format view count
const formatViews = (views) => {
  if (!views) return null;
  if (views >= 1000000000) return `${(views / 1000000000).toFixed(1)}B`;
  if (views >= 1000000) return `${Math.round(views / 1000000)}M`;
  if (views >= 1000) return `${Math.round(views / 1000)}K`;
  return views.toString();
};

// Clickable Tag Component
const ClickableTag = ({ id, name, type, onClick, showIcon = true, size = 'sm' }) => {
  const Icon = TYPE_ICONS[type];
  const color = TYPE_COLORS[type];
  
  return (
    <button
      onClick={() => onClick(id, type)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all hover:scale-105 cursor-pointer ${
        size === 'lg' ? 'text-sm' : 'text-xs'
      }`}
      style={{ 
        backgroundColor: `${color}15`,
        color: color,
        border: `1px solid ${color}30`
      }}
      data-testid={`tag-${type}-${id}`}
    >
      {showIcon && <Icon className={size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} />}
      <span>{name}</span>
    </button>
  );
};

// Main Glossary Component
const Glossary = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [allData, setAllData] = useState({ artists: [], songs: [], words: [], themes: [] });
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'theme');
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemDetails, setSelectedItemDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [artistsRes, songsRes, wordsRes, themesRes, statsRes] = await Promise.all([
        axios.get(`${API}/artists`),
        axios.get(`${API}/songs`),
        axios.get(`${API}/words`),
        axios.get(`${API}/themes`),
        axios.get(`${API}/stats`)
      ]);
      
      setAllData({
        artists: artistsRes.data,
        songs: songsRes.data,
        words: wordsRes.data,
        themes: themesRes.data
      });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch item details
  const fetchItemDetails = useCallback(async (itemId) => {
    try {
      const response = await axios.get(`${API}/node/${itemId}`);
      setSelectedItemDetails(response.data);
    } catch (error) {
      console.error('Error fetching item details:', error);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Handle URL params for deep linking
  useEffect(() => {
    const itemId = searchParams.get('item');
    const category = searchParams.get('category');
    
    if (category && category !== activeCategory) {
      setActiveCategory(category);
    }
    
    if (itemId && allData.artists.length > 0) {
      // Find item in all data
      const item = findItemById(itemId);
      if (item) {
        setSelectedItem(item);
        fetchItemDetails(itemId);
      }
    }
  }, [searchParams, allData, fetchItemDetails]);

  // Find item by ID across all categories
  const findItemById = (id) => {
    for (const artist of allData.artists) {
      if (artist.id === id) return { ...artist, type: 'artist', displayName: artist.name };
    }
    for (const song of allData.songs) {
      if (song.id === id) return { ...song, type: 'song', displayName: song.title };
    }
    for (const word of allData.words) {
      if (word.id === id) return { ...word, type: 'word', displayName: word.word };
    }
    for (const theme of allData.themes) {
      if (theme.id === id) return { ...theme, type: 'theme', displayName: theme.name };
    }
    return null;
  };

  // Navigate to item
  const navigateToItem = (id, type) => {
    const item = findItemById(id);
    if (item) {
      setSelectedItem(item);
      fetchItemDetails(id);
      setSearchParams({ category: type, item: id });
    }
  };

  // Close detail view
  const closeDetail = () => {
    setSelectedItem(null);
    setSelectedItemDetails(null);
    setSearchParams({ category: activeCategory });
  };

  // Change category
  const changeCategory = (category) => {
    setActiveCategory(category);
    setSelectedItem(null);
    setSelectedItemDetails(null);
    setSearchParams({ category });
  };

  // Get current category items
  const getCategoryItems = () => {
    const categoryMap = {
      artist: allData.artists,
      song: allData.songs,
      word: allData.words,
      theme: allData.themes
    };
    return categoryMap[activeCategory] || [];
  };

  // Filter items by search
  const getFilteredItems = () => {
    const items = getCategoryItems();
    if (!searchQuery.trim()) return items;
    
    const query = searchQuery.toLowerCase();
    return items.filter(item => {
      const name = item.name || item.title || item.word || '';
      const description = item.description || '';
      return name.toLowerCase().includes(query) || description.toLowerCase().includes(query);
    });
  };

  // Get item display name
  const getItemName = (item) => item.name || item.title || item.word;

  // Render category card
  const renderCard = (item) => {
    const name = getItemName(item);
    const Icon = TYPE_ICONS[activeCategory];
    const color = TYPE_COLORS[activeCategory];
    
    return (
      <motion.button
        key={item.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={() => navigateToItem(item.id, activeCategory)}
        className="w-full text-left p-5 rounded-2xl bg-[#0a0a0a] border border-white/5 hover:border-white/20 transition-all group"
        style={{ '--accent-color': color }}
        data-testid={`card-${item.id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div 
            className="flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium"
            style={{ backgroundColor: `${color}15`, color }}
          >
            <Icon className="w-3 h-3" />
            {activeCategory.charAt(0).toUpperCase() + activeCategory.slice(1)}
          </div>
          {activeCategory === 'song' && item.views && (
            <div className="flex items-center gap-1 text-xs text-zinc-500">
              <Eye className="w-3 h-3" />
              {formatViews(item.views)}
            </div>
          )}
          {activeCategory === 'word' && item.count && (
            <div className="text-xs font-mono" style={{ color }}>
              ×{item.count}
            </div>
          )}
        </div>
        
        <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[var(--accent-color)] transition-colors">
          {name}
        </h3>
        
        {item.description && (
          <p className="text-sm text-zinc-500 line-clamp-2 mb-3">
            {item.description}
          </p>
        )}
        
        {activeCategory === 'artist' && (
          <p className="text-xs text-zinc-600">{item.super_bowl} ({item.year})</p>
        )}
        
        {activeCategory === 'song' && (
          <p className="text-xs text-zinc-600">{item.super_bowl} ({item.year})</p>
        )}
        
        {activeCategory === 'theme' && item.keywords && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {item.keywords.slice(0, 6).map((kw, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded-full bg-[#BD00FF]/10 text-[#BD00FF]/70 border border-[#BD00FF]/20">
                {kw}
              </span>
            ))}
            {item.keywords.length > 6 && (
              <span className="text-xs px-2 py-1 text-zinc-500">
                +{item.keywords.length - 6} more
              </span>
            )}
          </div>
        )}
        
        <div className="flex items-center gap-1 mt-4 text-xs text-zinc-600 group-hover:text-zinc-400 transition-colors">
          <span>View connections</span>
          <ChevronRight className="w-3 h-3" />
        </div>
      </motion.button>
    );
  };

  // Render detail view
  const renderDetailView = () => {
    if (!selectedItem || !selectedItemDetails) return null;
    
    const name = selectedItem.displayName;
    const type = selectedItemDetails.type;
    const data = selectedItemDetails.data;
    const Icon = TYPE_ICONS[type];
    const color = TYPE_COLORS[type];

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-[#030303]/95 overflow-y-auto"
        data-testid="detail-view"
      >
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Back button */}
          <button
            onClick={closeDetail}
            className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors mb-8"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to {TYPE_LABELS[activeCategory]}</span>
          </button>
          
          {/* Header */}
          <div className="mb-8">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
              style={{ backgroundColor: `${color}15`, color }}
            >
              <Icon className="w-4 h-4" />
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </div>
            
            <h1 className="text-4xl font-bold text-white mb-2">{name}</h1>
            
            {type === 'word' && data?.count && (
              <p className="text-lg" style={{ color }}>
                Appears {data.count} times across halftime shows
              </p>
            )}
            
            {type === 'theme' && data?.description && (
              <p className="text-lg text-zinc-400 mt-2">{data.description}</p>
            )}
            
            {/* Theme Keywords */}
            {type === 'theme' && data?.keywords && (
              <div className="mt-4">
                <div className="text-sm text-zinc-500 mb-2">Keywords:</div>
                <div className="flex flex-wrap gap-2">
                  {data.keywords.map((kw, i) => (
                    <span 
                      key={i} 
                      className="text-sm px-3 py-1.5 rounded-full bg-[#BD00FF]/10 text-[#BD00FF] border border-[#BD00FF]/30"
                    >
                      {kw}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {type === 'artist' && data && (
              <p className="text-lg text-zinc-500">{data.super_bowl} ({data.year})</p>
            )}
            
            {type === 'song' && data && (
              <div className="flex items-center gap-4 mt-2">
                <span className="text-zinc-500">{data.year}</span>
                {data.views && (
                  <span className="flex items-center gap-1" style={{ color: TYPE_COLORS.song }}>
                    <Eye className="w-4 h-4" />
                    {formatViews(data.views)} views
                  </span>
                )}
              </div>
            )}
          </div>
          
          {/* Song details */}
          {type === 'song' && data && (
            <div className="space-y-6 mb-8">
              {data.description && (
                <div className="p-6 rounded-2xl bg-[#0a0a0a] border border-white/5">
                  <div className="flex items-center gap-2 text-zinc-500 text-sm mb-3">
                    <FileText className="w-4 h-4" />
                    About this song
                  </div>
                  <p className="text-zinc-300 leading-relaxed">{data.description}</p>
                </div>
              )}
              
              {data.lyrics && (
                <div className="p-6 rounded-2xl bg-gradient-to-br from-[#FF0055]/10 to-transparent border border-[#FF0055]/20">
                  <div className="flex items-center gap-2 text-[#FF0055] text-sm mb-3">
                    <Mic2 className="w-4 h-4" />
                    Lyrics Excerpt
                  </div>
                  <p className="text-zinc-300 leading-relaxed italic whitespace-pre-line">
                    "{data.lyrics}"
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Connections */}
          <div className="space-y-8">
            {/* Artists */}
            {selectedItemDetails.artists && selectedItemDetails.artists.length > 0 && (
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold mb-4" style={{ color: TYPE_COLORS.artist }}>
                  <User className="w-5 h-5" />
                  Artists ({selectedItemDetails.artists.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedItemDetails.artists.map(artist => (
                    <button
                      key={artist.id}
                      onClick={() => navigateToItem(artist.id, 'artist')}
                      className="flex items-center justify-between p-4 rounded-xl bg-[#0a0a0a] border border-white/5 hover:border-[#0AFF64]/30 transition-all group"
                      data-testid={`link-artist-${artist.id}`}
                    >
                      <div>
                        <div className="font-medium text-white group-hover:text-[#0AFF64] transition-colors">
                          {artist.name}
                        </div>
                        <div className="text-xs text-zinc-600">{artist.super_bowl}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-[#0AFF64] transition-colors" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Songs */}
            {selectedItemDetails.songs && selectedItemDetails.songs.length > 0 && (
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold mb-4" style={{ color: TYPE_COLORS.song }}>
                  <Music className="w-5 h-5" />
                  Songs ({selectedItemDetails.songs.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedItemDetails.songs.map(song => (
                    <button
                      key={song.id}
                      onClick={() => navigateToItem(song.id, 'song')}
                      className="flex items-center justify-between p-4 rounded-xl bg-[#0a0a0a] border border-white/5 hover:border-[#FF0055]/30 transition-all group"
                      data-testid={`link-song-${song.id}`}
                    >
                      <div>
                        <div className="font-medium text-white group-hover:text-[#FF0055] transition-colors">
                          {song.title}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-zinc-600">
                          <span>{song.year}</span>
                          {song.views && (
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {formatViews(song.views)}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-[#FF0055] transition-colors" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Themes */}
            {selectedItemDetails.themes && selectedItemDetails.themes.length > 0 && (
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold mb-4" style={{ color: TYPE_COLORS.theme }}>
                  <Sparkles className="w-5 h-5" />
                  Themes ({selectedItemDetails.themes.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {selectedItemDetails.themes.map(theme => (
                    <button
                      key={theme.id}
                      onClick={() => navigateToItem(theme.id, 'theme')}
                      className="flex items-center justify-between p-4 rounded-xl bg-[#0a0a0a] border border-white/5 hover:border-[#BD00FF]/30 transition-all group text-left"
                      data-testid={`link-theme-${theme.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-white group-hover:text-[#BD00FF] transition-colors">
                          {theme.name}
                        </div>
                        <div className="text-xs text-zinc-600 truncate">{theme.description}</div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-[#BD00FF] transition-colors flex-shrink-0 ml-2" />
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Words */}
            {selectedItemDetails.words && selectedItemDetails.words.length > 0 && (
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold mb-4" style={{ color: TYPE_COLORS.word }}>
                  <Hash className="w-5 h-5" />
                  Words ({selectedItemDetails.words.length})
                </h2>
                <div className="flex flex-wrap gap-2">
                  {selectedItemDetails.words.map(word => (
                    <button
                      key={word.id}
                      onClick={() => navigateToItem(word.id, 'word')}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full transition-all hover:scale-105 cursor-pointer text-sm"
                      style={{ 
                        backgroundColor: `${TYPE_COLORS.word}15`,
                        color: TYPE_COLORS.word,
                        border: `1px solid ${TYPE_COLORS.word}30`
                      }}
                      data-testid={`link-word-${word.id}`}
                    >
                      <Hash className="w-3 h-3" />
                      <span>{word.word}</span>
                      <span className="opacity-60">({word.count})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const filteredItems = getFilteredItems();
  const categories = ['theme', 'artist', 'song', 'word'];

  return (
    <div className="min-h-screen bg-[#030303]" data-testid="glossary">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#030303]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Disc3 className="w-8 h-8 text-[#00F0FF]" />
              <div>
                <h1 className="text-xl font-bold text-white">American Culture Map</h1>
                <p className="text-xs text-zinc-500">Super Bowl Halftime Show Analysis</p>
              </div>
            </div>
            
            {/* Stats */}
            {stats && (
              <div className="flex items-center gap-6 text-sm">
                <div className="text-center">
                  <div className="text-lg font-bold" style={{ color: TYPE_COLORS.artist }}>{stats.total_artists}</div>
                  <div className="text-xs text-zinc-500">Artists</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold" style={{ color: TYPE_COLORS.song }}>{stats.total_songs}</div>
                  <div className="text-xs text-zinc-500">Songs</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold" style={{ color: TYPE_COLORS.word }}>{stats.total_words}</div>
                  <div className="text-xs text-zinc-500">Words</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold" style={{ color: TYPE_COLORS.theme }}>{stats.total_themes}</div>
                  <div className="text-xs text-zinc-500">Themes</div>
                </div>
              </div>
            )}
          </div>
          
          {/* Category Tabs */}
          <div className="flex items-center gap-2">
            {categories.map(cat => {
              const Icon = TYPE_ICONS[cat];
              const isActive = activeCategory === cat;
              const color = TYPE_COLORS[cat];
              
              return (
                <button
                  key={cat}
                  onClick={() => changeCategory(cat)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    isActive ? 'text-white' : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                  style={{
                    backgroundColor: isActive ? `${color}20` : 'transparent',
                    borderColor: isActive ? color : 'transparent',
                    border: `1px solid ${isActive ? color + '50' : 'transparent'}`
                  }}
                  data-testid={`tab-${cat}`}
                >
                  <Icon className="w-4 h-4" style={{ color: isActive ? color : undefined }} />
                  {TYPE_LABELS[cat]}
                </button>
              );
            })}
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="text"
            placeholder={`Search ${TYPE_LABELS[activeCategory].toLowerCase()}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-12 py-4 rounded-xl bg-[#0a0a0a] border border-white/10 text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 transition-colors"
            data-testid="search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              data-testid="clear-search"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        
        {/* Results count */}
        <div className="mb-6 text-sm text-zinc-500">
          Showing {filteredItems.length} of {getCategoryItems().length} {TYPE_LABELS[activeCategory].toLowerCase()}
        </div>
        
        {/* Cards Grid */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Disc3 className="w-12 h-12 text-[#00F0FF] animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence mode="popLayout">
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.03 }}
                >
                  {renderCard(item)}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
        
        {!isLoading && filteredItems.length === 0 && (
          <div className="text-center py-20">
            <p className="text-zinc-500">No {TYPE_LABELS[activeCategory].toLowerCase()} found matching your search.</p>
          </div>
        )}
      </main>
      
      {/* Detail View Modal */}
      <AnimatePresence>
        {selectedItem && renderDetailView()}
      </AnimatePresence>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Glossary />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
